import { useEffect, useState } from "react";
import { Container, Label, Form, FormGroup } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import toast from "react-hot-toast";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import typography from "../typography";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { getLockedSessionContent, updateLockedSessionContent } from "../../../api/api.service";
import { initializeMediaUtil, uploadOnS3, finalizeMediaUtil } from "../../../utils/mediaUtils";
import { ALLOWED_IMAGE_EXTENSIONS } from "../data";

// One locked session's editable copy and thumbnail — the fields
// DynamicActivityPreview.tsx's INFO_SECTIONS already shows for every other
// activity, editable here too even though calm/focus have no
// AudioVideoContent row (see LockedSessions.js).
//
// Reached from the LockedSessions listing, mirroring the audio/video
// list-then-edit flow. It used to be a card rendered inline on the listing
// itself, two at a time.
const LOCKED_SESSION_LABELS = {
    calm: { stateLabel: typography.CALM, defaultTitle: "Micro Reset" },
    focus: { stateLabel: typography.FOCUS, defaultTitle: "Calm Me Now" },
};

// "" -> null so the backend clears the field rather than skipping it; see
// the note in handleSaveClick.
const emptyToNull = (value) => value?.trim() || null;

export default function EditLockedSession() {
    const { stateKey } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailFileError, setThumbnailFileError] = useState("");
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const labels = LOCKED_SESSION_LABELS[stateKey];

    const formik = useFormik({
        initialValues: { title: "", goal: "", description: "", teacherInstructions: "" },
        enableReinitialize: false,
        onSubmit: handleSaveClick,
    });

    useEffect(() => {
        if (!labels) {
            setLoadError("Unknown activity.");
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        getLockedSessionContent(stateKey)
            .then((content) => {
                if (cancelled) return;
                // The API helper resolves with the parsed response body
                // itself — there is no `.data` envelope around it (see
                // api.helper.js / fetchUtil.js, and ViewContent.js which
                // consumes its response the same way). Reading `res.data`
                // here was why every saved value came back blank on this
                // screen while the teacher app showed it correctly: the
                // fetch succeeded, `res.data` was undefined, and the form
                // was left on its empty defaults.
                if (!content) return;
                formik.setValues({
                    title: content.title || "",
                    goal: content.goal || "",
                    description: content.description || "",
                    teacherInstructions: content.teacherInstructions || "",
                });
                setThumbnailUrl(content.thumbnailUrl || null);
            })
            .catch(() => {
                if (!cancelled) setLoadError("Failed to load this activity.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line
    }, [stateKey]);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.LOCKED_SESSION_CONTENT);
    };

    const handleThumbnailFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
            setThumbnailFileError(`Unsupported file type. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`);
            setThumbnailFile(null);
            return;
        }

        setThumbnailFileError("");
        setThumbnailFile(selected);
    };

    // Same IMAGE upload flow AddContent.js uses for its thumbnail field.
    const handleThumbnailUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "IMAGE");
        await uploadOnS3(selectedFile, credentials, setThumbnailUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    async function handleSaveClick(values) {
        try {
            setIsSaving(true);
            const thumbnailMedia = thumbnailFile ? await handleThumbnailUpload(thumbnailFile) : null;

            const updated = await updateLockedSessionContent({
                stateKey,
                bodyData: {
                    // null, not undefined, for a field the admin has cleared.
                    // The backend treats undefined as "leave this alone" and
                    // null as "clear it" — sending undefined here meant an
                    // admin could set a value but never take it back, and
                    // emptying the box then saving silently did nothing.
                    // Clearing is how you revert to the built-in name/copy.
                    title: emptyToNull(values.title),
                    goal: emptyToNull(values.goal),
                    description: emptyToNull(values.description),
                    teacherInstructions: emptyToNull(values.teacherInstructions),
                    ...(thumbnailMedia && { thumbnailMediaId: thumbnailMedia.id }),
                },
            });

            // Re-seed straight from what the server actually stored, so this
            // screen shows the saved state rather than whatever was typed —
            // the same reason the listing re-fetches on mount.
            if (updated) {
                formik.setValues({
                    title: updated.title || "",
                    goal: updated.goal || "",
                    description: updated.description || "",
                    teacherInstructions: updated.teacherInstructions || "",
                });
                setThumbnailUrl(updated.thumbnailUrl || null);
            }
            setThumbnailFile(null);
            toast.success("Activity updated");
        } catch (error) {
            console.error(`Error updating ${stateKey} locked session:`, error);
            toast.error("Failed to save. Please try again.");
        } finally {
            setIsSaving(false);
            setThumbnailUploadProgress(0);
        }
    }

    if (loading) return <LoaderFallbackUI />;

    if (loadError) {
        return (
            <div className="page-content">
                <Container fluid className="customer-management">
                    <div className="edit-notification-heading">
                        <HeadingWithBackArrow
                            backArrowClickHandler={backButtonHandler}
                            headingTitle={typography.EDIT_LOCKED_SESSION}
                            showToggle={false}
                        />
                    </div>
                    <div className="standard-typo-14-400">{loadError}</div>
                </Container>
            </div>
        );
    }

    // page-content + .customer-management, same as the audio/video edit page.
    // Every Content Library style is nested under that class in Styles.scss,
    // so a page rendered outside it gets none of them — including any sizing
    // on the thumbnail preview below.
    return (
        <div className="page-content">
            <Container fluid className="customer-management">
                <div className="edit-notification-heading">
                    <HeadingWithBackArrow
                        backArrowClickHandler={backButtonHandler}
                        headingTitle={`${typography.EDIT_LOCKED_SESSION} — ${labels.stateLabel}`}
                        showToggle={false}
                    />
                </div>

                <Form onSubmit={formik.handleSubmit} className="edit-notification-form">
                <FormGroup>
                    <Label className="edit-notification-form-label" for="locked-session-title">
                        {typography.TITLE}
                    </Label>
                    <div className="standard-typo-14-400 mb-2">{typography.TITLE_HELP}</div>
                    <TextInput
                        id="locked-session-title"
                        name="title"
                        placeholder={labels.defaultTitle}
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="edit-notification-form-input hide-default-icon"
                    />
                </FormGroup>

                <FormGroup>
                    <Label className="edit-notification-form-label" for="locked-session-goal">
                        {typography.GOAL}
                    </Label>
                    <div className="standard-typo-14-400 mb-2">{typography.GOAL_HELP}</div>
                    <TextInput
                        id="locked-session-goal"
                        name="goal"
                        placeholder="Enter goal (optional)"
                        value={formik.values.goal}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="edit-notification-form-input hide-default-icon"
                    />
                </FormGroup>

                <FormGroup>
                    <Label className="edit-notification-form-label" for="locked-session-description">
                        {typography.DESCRIPTION}
                    </Label>
                    <textarea
                        id="locked-session-description"
                        name="description"
                        className="edit-notification-form-textarea"
                        placeholder="Enter description (optional)"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={3}
                    />
                </FormGroup>

                <FormGroup>
                    <Label className="edit-notification-form-label" for="locked-session-teacher-instructions">
                        {typography.TEACHER_INSTRUCTIONS}
                    </Label>
                    <div className="standard-typo-14-400 mb-2">{typography.TEACHER_INSTRUCTIONS_HELP}</div>
                    <textarea
                        id="locked-session-teacher-instructions"
                        name="teacherInstructions"
                        className="edit-notification-form-textarea"
                        placeholder="Enter teacher instructions (optional)"
                        value={formik.values.teacherInstructions}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={3}
                    />
                </FormGroup>

                <FormGroup>
                    <Label className="edit-notification-form-label" for="locked-session-thumbnail-file">
                        {typography.THUMBNAIL}
                    </Label>
                    <div className="content-file-upload-con">
                        {thumbnailUrl && (
                            // Show the thumbnail that is actually set, rather
                            // than only saying so in text — an admin
                            // returning to this screen should be able to see
                            // what teachers see without leaving it.
                            <img src={thumbnailUrl} alt="" className="locked-session-thumb-preview mb-2" />
                        )}
                        <input
                            id="locked-session-thumbnail-file"
                            name="thumbnailFile"
                            type="file"
                            accept={ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                            onChange={handleThumbnailFileChange}
                        />
                        {thumbnailFile ? (
                            <div className="mt-2 standard-typo-14-400">{thumbnailFile.name}</div>
                        ) : thumbnailUrl ? (
                            <div className="mt-2 standard-typo-14-400">Currently set — choose a file to replace</div>
                        ) : (
                            <div className="mt-2 standard-typo-14-400">{typography.NOT_UPLOADED}</div>
                        )}
                        {isSaving && thumbnailFile && (
                            <div className="content-file-progress">
                                <div
                                    className="content-file-progress-bar"
                                    style={{ width: `${thumbnailUploadProgress}%` }}
                                />
                            </div>
                        )}
                        {thumbnailFileError && <div className="invalid-feedback d-block">{thumbnailFileError}</div>}
                    </div>
                </FormGroup>

                <CustomButton
                    title={isSaving ? "Saving…" : "Save"}
                    className="edit-notification-update-button"
                    onClick={formik.handleSubmit}
                    type="submit"
                    loading={isSaving}
                />
                </Form>
            </Container>
        </div>
    );
}
